import { computed } from "vue";
import { useStore } from "vuex";

/**
 * Patient Info Store Helpers
 * Provides simplified access to patient info getters and actions
 */
export function usePatientInfoStore() {
  const store = useStore();

  // States
  const state = {
    patients: computed(() => store.state.myPatients.patientInfo.patients),
    pegboardCardVersion: computed(() => store.state.myPatients.patientInfo.pegboardCardVersion),
    pegboardCards: computed(() => store.state.myPatients.patientInfo.pegboardCards),
    currentPatientMedications: computed(() => store.state.myPatients.patientInfo.currentPatientMedications),
    currentPatientAllergies: computed(() => store.state.myPatients.patientInfo.currentPatientAllergies),
    medicationsLoading: computed(() => store.state.myPatients.patientInfo.medicationsLoading),
    allergiesLoading: computed(() => store.state.myPatients.patientInfo.allergiesLoading),
    currentPatientAppointments: computed(() => store.state.myPatients.patientInfo.currentPatientAppointments),
    appointmentsLoading: computed(() => store.state.myPatients.patientInfo.appointmentsLoading),
    currentPatientTasks: computed(() => store.state.myPatients.patientInfo.currentPatientTasks),
    tasksLoading: computed(() => store.state.myPatients.patientInfo.tasksLoading),
    currentPatientHealthInfo: computed(() => store.state.myPatients.patientInfo.currentPatientHealthInfo),
    healthInfoLoading: computed(() => store.state.myPatients.patientInfo.healthInfoLoading),
    currentPatientBillingOrders: computed(() => store.state.myPatients.patientInfo.currentPatientBillingOrders),
    billingOrdersLoading: computed(() => store.state.myPatients.patientInfo.billingOrdersLoading),
    currentPatientDocuments: computed(() => store.state.myPatients.patientInfo.currentPatientDocuments),
    documentsLoading: computed(() => store.state.myPatients.patientInfo.documentsLoading),
    pegboardLoading: computed(() => store.state.myPatients.patientInfo.pegboardLoading),
  };

  // Getters
  const getters = {
    getPatientById: (id) => store.getters["myPatients/patientInfo/getPatientById"](id),
    getPegboardCardId: (cardData) => store.getters["myPatients/patientInfo/getPegboardCardId"](cardData),
    isCardPinned: (cardData) => store.getters["myPatients/patientInfo/isCardPinned"](cardData),
    getPegboardCards: computed(() => store.getters["myPatients/patientInfo/getPegboardCards"]),
    getAllAppointments: computed(() => store.getters["myPatients/patientInfo/getAllAppointments"]),
    getAllTasks: computed(() => store.getters["myPatients/patientInfo/getAllTasks"]),

    // Card Data Getters
    getContactCardData: (patient) => store.getters["myPatients/patientInfo/getContactCardData"](patient),
    getDemographicsCardData: (patient) => store.getters["myPatients/patientInfo/getDemographicsCardData"](patient),
    getBillingCardData: (patient) => store.getters["myPatients/patientInfo/getBillingCardData"](patient),
    getMedicationCardData: (patient) => store.getters["myPatients/patientInfo/getMedicationCardData"](patient),
    getTaskCardData: computed(() => store.getters["myPatients/patientInfo/getTaskCardData"]),
    getFormCardData: (patient) => store.getters["myPatients/patientInfo/getFormCardData"](patient),
    getQuestionnaireCardData: (patient) => store.getters["myPatients/patientInfo/getQuestionnaireCardData"](patient),
    getAppointmentCardData: computed(() => store.getters["myPatients/patientInfo/getAppointmentCardData"]),
    getAllergyCardData: (patient) => store.getters["myPatients/patientInfo/getAllergyCardData"](patient),
    getNotesCardData: (patient) => store.getters["myPatients/patientInfo/getNotesCardData"](patient),
    getSampleCardData: (patient, entityId) => store.getters["myPatients/patientInfo/getSampleCardData"](patient, entityId),
    getOrderHandlingCardData: (patient) => store.getters["myPatients/patientInfo/getOrderHandlingCardData"](patient),
    getCurrentPatientContactInfo: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientContactInfo"]),
    getCurrentPatientEmergencyContact: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientEmergencyContact"]),
    getCurrentPatientDemographics: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientDemographics"]),
    getCurrentPatientInfoLoading: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientInfoLoading"]),
    getCurrentPatientRawData: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientRawData"]),
    getCurrentPatientMedications: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientMedications"]),
    getCurrentPatientAllergies: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientAllergies"]),
    getMedicationsLoading: computed(() => store.getters["myPatients/patientInfo/getMedicationsLoading"]),
    getAllergiesLoading: computed(() => store.getters["myPatients/patientInfo/getAllergiesLoading"]),
    getCurrentPatientAppointments: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientAppointments"]),
    getAppointmentsLoading: computed(() => store.getters["myPatients/patientInfo/getAppointmentsLoading"]),
    getCurrentPatientTasks: computed(() => store.getters["myPatients/patientInfo/getCurrentPatientTasks"]),
    getTasksLoading: computed(() => store.getters["myPatients/patientInfo/getTasksLoading"]),
    getCurrentInternalNoteId: computed(() => store.getters["myPatients/patientInfo/getCurrentInternalNoteId"]),
  };

  // Actions
  const actions = {
    // Add patient info actions here as needed
    updateContactInfo: (payload) => store.dispatch("myPatients/patientInfo/updateContactInfo", payload),
    updateEmergencyContact: (payload) => store.dispatch("myPatients/patientInfo/updateEmergencyContact", payload),
    initContactInfo: (payload) => store.dispatch("myPatients/patientInfo/initContactInfo", payload),
    initEmergencyContact: (payload) => store.dispatch("myPatients/patientInfo/initEmergencyContact", payload),
    updateDemographics: (payload) => store.dispatch("myPatients/patientInfo/updateDemographics", payload),
    getPegboardList: (payload) => store.dispatch("myPatients/patientInfo/getPegboardList", payload),
    pinCard: (payload) => store.dispatch("myPatients/patientInfo/pinCard", payload),
    unpinCard: (payload) => store.dispatch("myPatients/patientInfo/unpinCard", payload),
    updatePegboardCardsOrder: (payload) => store.dispatch("myPatients/patientInfo/updatePegboardCardsOrder", payload),
    fetchCurrentPatientMedications: (patientId) => store.dispatch("myPatients/patientInfo/fetchCurrentPatientMedications", patientId),
    fetchCurrentPatientAllergies: (patientId) => store.dispatch("myPatients/patientInfo/fetchCurrentPatientAllergies", patientId),
    updateMedication: (medication) => store.dispatch("myPatients/patientInfo/updateMedication", medication),
    addMedication: (medication) => store.dispatch("myPatients/patientInfo/addMedication", medication),
    removeMedication: (medicationId) => store.dispatch("myPatients/patientInfo/removeMedication", medicationId),
    updateAllergy: (allergy) => store.dispatch("myPatients/patientInfo/updateAllergy", allergy),
    addAllergy: (allergy) => store.dispatch("myPatients/patientInfo/addAllergy", allergy),
    removeAllergy: (allergyId) => store.dispatch("myPatients/patientInfo/removeAllergy", allergyId),
    setCurrentInternalNoteId: (noteId) => store.dispatch("myPatients/patientInfo/setCurrentInternalNoteId", noteId),
    fetchPatientAppointments: (patientId) => store.dispatch("myPatients/patientInfo/fetchPatientAppointments", patientId),
    fetchPatientTasks: (patientId) => store.dispatch("myPatients/patientInfo/fetchPatientTasks", patientId),
    fetchCurrentPatientBillingOrders: (patientId) => store.dispatch("myPatients/patientInfo/fetchCurrentPatientBillingOrders", patientId),
    fetchCurrentPatientDocuments: (patientId) => store.dispatch("myPatients/patientInfo/fetchCurrentPatientDocuments", patientId),
    fetchCurrentPatientVitals: (patientId) => store.dispatch("myPatients/patientInfo/fetchCurrentPatientVitals", patientId),
    fetchPatientById: (patientId) => store.dispatch("myPatients/patientInfo/fetchPatientById", patientId),
    fetchPatientCalendarIdMap: () => store.dispatch("myPatients/patientInfo/fetchPatientCalendarIdMap"),
    getPatientCalendarId: (patientId) => store.getters["myPatients/patientInfo/getPatientCalendarId"](patientId),
  };

  return {
    state,
    getters,
    actions,
  };
}
